import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ShoppingCart, Clock, Users, Zap, Star, Shield, Headphones, Code, Palette, Smartphone, Bot, Settings, Rocket, ArrowRight, Play, CheckCircle2, Sparkles, TrendingUp, Globe, Layers, Tag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import HowItWorksAnimated from '../components/HowItWorksAnimated';
import AnimatedShowcase from '../components/AnimatedShowcase';
import FloatingOfferBadge from '../components/FloatingOfferBadge';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DISCOUNT_PERCENT = 40;

// Service-specific hero configurations
const serviceHeroConfig = {
  'website-development': { // Custom Websites
    icon: Globe,
    gradient: 'from-[#3B82F6] to-[#06B6D4]',
    accentColor: '#3B82F6',
    highlights: [
      { icon: Rocket, text: 'Lightning Fast', value: '<1s Load' },
      { icon: Shield, text: 'SEO Optimized', value: 'Top Rankings' },
      { icon: Smartphone, text: 'Mobile First', value: '100% Responsive' },
      { icon: Palette, text: 'Custom Design', value: 'Unique Brand' }
    ],
    techStack: ['React', 'Next.js', 'Tailwind', 'Node.js', 'AWS'],
    stats: { projects: '150+', satisfaction: '99%', avgDelivery: '4 weeks' }
  },
  'app-development': { // Mobile & Web Apps
    icon: Smartphone,
    gradient: 'from-[#8B5CF6] to-[#EC4899]',
    accentColor: '#8B5CF6',
    highlights: [
      { icon: Layers, text: 'Cross Platform', value: 'iOS & Android' },
      { icon: Zap, text: 'Native Performance', value: '60 FPS' },
      { icon: Shield, text: 'Secure Backend', value: 'End-to-End' },
      { icon: TrendingUp, text: 'Scalable', value: '1M+ Users' }
    ],
    techStack: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase'],
    stats: { projects: '80+', satisfaction: '98%', avgDelivery: '8 weeks' }
  },
  'uiux-design': { // UI/UX & Graphic Design
    icon: Palette,
    gradient: 'from-[#F97316] to-[#EAB308]',
    accentColor: '#F97316',
    highlights: [
      { icon: Sparkles, text: 'Modern Aesthetics', value: '2024 Trends' },
      { icon: Users, text: 'User Research', value: 'Data Driven' },
      { icon: Layers, text: 'Design Systems', value: 'Scalable UI' },
      { icon: CheckCircle2, text: 'Accessibility', value: 'WCAG 2.1' }
    ],
    techStack: ['Figma', 'Adobe XD', 'After Effects', 'Illustrator', 'Principle'],
    stats: { projects: '200+', satisfaction: '100%', avgDelivery: '2 weeks' }
  },
  'ai-solutions': { // Agentic AI Development
    icon: Bot,
    gradient: 'from-[#10B981] to-[#3B82F6]',
    accentColor: '#10B981',
    highlights: [
      { icon: Bot, text: 'Custom AI Agents', value: 'GPT-5.2' },
      { icon: Zap, text: 'Automation', value: '90% Tasks' },
      { icon: TrendingUp, text: 'ROI Boost', value: '300%+' },
      { icon: Shield, text: 'Enterprise Ready', value: 'SOC 2' }
    ],
    techStack: ['OpenAI', 'LangChain', 'Python', 'Vector DBs', 'RAG'],
    stats: { projects: '50+', satisfaction: '99%', avgDelivery: '6 weeks' }
  },
  'business-tools': { // Business Automation
    icon: Settings,
    gradient: 'from-[#EC4899] to-[#8B5CF6]',
    accentColor: '#EC4899',
    highlights: [
      { icon: Clock, text: 'Time Saved', value: '40+ hrs/week' },
      { icon: TrendingUp, text: 'Efficiency', value: '+250%' },
      { icon: Layers, text: 'Integrations', value: '100+ Apps' },
      { icon: CheckCircle2, text: 'Error Free', value: '99.9%' }
    ],
    techStack: ['Zapier', 'n8n', 'Python', 'APIs', 'Webhooks'],
    stats: { projects: '120+', satisfaction: '98%', avgDelivery: '3 weeks' }
  }
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeHighlight, setActiveHighlight] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isOfferActive, setIsOfferActive] = useState(false);

  const heroConfig = serviceHeroConfig[serviceId] || serviceHeroConfig['website-development'];
  const HeroIcon = heroConfig.icon;

  useEffect(() => {
    fetchServiceDetail();
    checkOfferStatus();
    setIsVisible(true);
    
    // Auto-rotate highlights
    const interval = setInterval(() => {
      setActiveHighlight(prev => (prev + 1) % 4);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [serviceId]);

  const checkOfferStatus = () => {
    const offerEndDate = localStorage.getItem('offer_end_date');
    if (offerEndDate) {
      const endDate = new Date(offerEndDate);
      const now = new Date();
      setIsOfferActive(now < endDate);
    }
  };

  const getDiscountedPrice = (price) => {
    if (!isOfferActive) return price;
    return price * (1 - DISCOUNT_PERCENT / 100);
  };

  const fetchServiceDetail = async () => {
    try {
      const response = await axios.get(`${API}/services/${serviceId}`);
      setService(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching service:', error);
      toast.error('Service not found');
      navigate('/');
    }
  };

  const getSessionId = () => {
    let sessionId = localStorage.getItem('cart_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem('cart_session_id', sessionId);
    }
    return sessionId;
  };

  const buyNow = (pkg) => {
    // Direct checkout - navigate to checkout with service and package info
    const params = new URLSearchParams({
      service_id: service.id,
      service_name: service.name,
      package_id: pkg.id,
      package_name: pkg.name,
      price: isOfferActive ? getDiscountedPrice(pkg.price) : pkg.price
    });
    navigate(`/checkout?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-dark)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
          <div className="text-[var(--text-muted)]">Loading service...</div>
        </div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  // SEO configuration based on service
  const seoConfig = {
    'website-development': {
      title: 'Custom Website Development Services in Buffalo NY',
      description: 'Professional website development in Cheektowaga, NY. Enterprise-grade responsive websites built with React, Next.js & modern technologies. 40% OFF! Call 888-383-3213.',
      keywords: 'website development Buffalo NY, custom websites Cheektowaga, responsive web design, React developer Buffalo, web development company near me'
    },
    'app-development': {
      title: 'Mobile App Development - iOS & Android Apps in Buffalo NY',
      description: 'Custom mobile app development in Western NY. Native iOS, Android & cross-platform apps with React Native & Flutter. Get 40% OFF all services!',
      keywords: 'mobile app development Buffalo NY, iOS app developer Cheektowaga, Android app development, React Native developer near me, app development company Buffalo'
    },
    'uiux-design': {
      title: 'UI/UX Design & Brand Identity Services in Buffalo NY',
      description: 'Modern UI/UX design services in Cheektowaga, NY. User research, wireframing, prototyping & brand identity systems. 40% OFF! Professional design agency.',
      keywords: 'UI UX design Buffalo NY, graphic design Cheektowaga, brand identity design, Figma designer near me, web design agency Buffalo'
    },
    'ai-solutions': {
      title: 'AI Solutions & Agentic AI Development in Buffalo NY',
      description: 'Custom AI solutions and intelligent automation in Western NY. GPT-powered chatbots, AI agents & business automation. Enterprise-ready AI development.',
      keywords: 'AI solutions Buffalo NY, AI development Cheektowaga, chatbot development, machine learning services, AI automation company near me'
    },
    'business-tools': {
      title: 'Business Automation & CRM Tools in Buffalo NY',
      description: 'Business automation services in Cheektowaga, NY. Custom CRM systems, workflow automation & cloud integrations. Save 40+ hours/week with our solutions.',
      keywords: 'business automation Buffalo NY, CRM development Cheektowaga, workflow automation, Zapier integrations, business tools development'
    }
  };

  const currentSeo = seoConfig[serviceId] || seoConfig['website-development'];

  // Structured data for service
  const serviceStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "CLOUD IT",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Cheektowaga",
        "addressRegion": "NY",
        "postalCode": "14227",
        "addressCountry": "US"
      },
      "telephone": "+1-888-383-3213"
    },
    "areaServed": {
      "@type": "City",
      "name": "Buffalo"
    },
    "offers": service.packages?.map(pkg => ({
      "@type": "Offer",
      "name": pkg.name,
      "price": pkg.price,
      "priceCurrency": "USD"
    }))
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <SEO title={currentSeo.title} />
      <Header />
      
      {/* Enhanced Animated Hero Section */}
      <section className="pt-28 pb-12 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className={`absolute top-0 left-1/4 w-[800px] h-[800px] bg-gradient-to-r ${heroConfig.gradient} opacity-10 rounded-full blur-[200px] animate-pulse`}></div>
          <div className={`absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-gradient-to-r ${heroConfig.gradient} opacity-5 rounded-full blur-[150px] animate-pulse`} style={{ animationDelay: '1s' }}></div>
          
          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 rounded-full opacity-30"
                style={{
                  background: heroConfig.accentColor,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 5}s`
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-[7.6923%]">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-300 mb-6 group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm">Back to Services</span>
          </button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Service Info */}
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Service Badge with Icon */}
              <div className="flex items-center gap-3 mb-5">
                <div 
                  className={`w-12 h-12 rounded-xl bg-gradient-to-r ${heroConfig.gradient} flex items-center justify-center shadow-lg`}
                  style={{ boxShadow: `0 10px 25px ${heroConfig.accentColor}30` }}
                >
                  <HeroIcon size={24} className="text-white" />
                </div>
                <div className={`px-3 py-1.5 rounded-full bg-gradient-to-r ${heroConfig.gradient} bg-opacity-10 border`} style={{ borderColor: `${heroConfig.accentColor}30` }}>
                  <span style={{ color: heroConfig.accentColor }} className="text-xs font-semibold uppercase tracking-wider">{service.name}</span>
                </div>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-[var(--text-primary)] mb-4 leading-tight" style={{ letterSpacing: '-0.02em' }}>
                {service.tagline?.split(' ').slice(0, 3).join(' ')}{' '}
                <span className={`bg-gradient-to-r ${heroConfig.gradient} bg-clip-text text-transparent`}>
                  {service.tagline?.split(' ').slice(3).join(' ') || 'Excellence'}
                </span>
              </h1>
              
              <p className="text-base text-[var(--text-secondary)] mb-6 leading-relaxed max-w-lg">
                {service.description}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  className={`px-6 py-3 text-sm font-semibold text-white rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center gap-2 bg-gradient-to-r ${heroConfig.gradient}`}
                  style={{ boxShadow: `0 8px 25px ${heroConfig.accentColor}30` }}
                >
                  View Packages
                  <ArrowRight size={16} />
                </button>
                <button
                  onClick={() => navigate('/book-consultation')}
                  className="px-6 py-3 text-sm font-semibold text-[var(--text-primary)] rounded-xl transition-all duration-300 hover:scale-105 border border-[var(--border)] hover:border-[var(--border-hover)] bg-[var(--bg-card)] flex items-center gap-2"
                >
                  <Play size={16} className="text-[var(--text-muted)]" />
                  Free Consultation
                </button>
              </div>

              {/* Tech Stack Pills */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[var(--text-muted)] text-xs mr-1">Built with:</span>
                {heroConfig.techStack.map((tech, idx) => (
                  <span
                    key={idx}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-hover)] transition-colors cursor-default"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Right Side - Interactive Feature Cards */}
            <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Main Feature Showcase */}
              <div className="relative">
                {/* Animated Glow Ring */}
                <div 
                  className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${heroConfig.gradient} opacity-20 blur-xl animate-pulse`}
                ></div>
                
                {/* Feature Cards Grid */}
                <div className="relative grid grid-cols-2 gap-4">
                  {heroConfig.highlights.map((highlight, idx) => {
                    const HighlightIcon = highlight.icon;
                    const isActive = idx === activeHighlight;
                    
                    return (
                      <div
                        key={idx}
                        onClick={() => setActiveHighlight(idx)}
                        className={`relative p-6 rounded-2xl border transition-all duration-500 cursor-pointer group ${
                          isActive 
                            ? 'bg-[var(--bg-card)] border-transparent scale-105 shadow-2xl' 
                            : 'bg-[var(--bg-card)]/50 border-[var(--border)] hover:bg-[var(--bg-card)] hover:border-[var(--border-hover)]'
                        }`}
                        style={isActive ? { 
                          boxShadow: `0 20px 60px ${heroConfig.accentColor}30`,
                          borderColor: heroConfig.accentColor
                        } : {}}
                      >
                        {/* Active Indicator */}
                        {isActive && (
                          <div 
                            className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r ${heroConfig.gradient}`}
                          ></div>
                        )}
                        
                        <div 
                          className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${
                            isActive ? '' : 'bg-[var(--bg-dark)]'
                          }`}
                          style={isActive ? { 
                            background: `linear-gradient(135deg, ${heroConfig.accentColor}20, ${heroConfig.accentColor}10)` 
                          } : {}}
                        >
                          <HighlightIcon 
                            size={24} 
                            style={{ color: isActive ? heroConfig.accentColor : 'var(--text-muted)' }}
                            className="transition-colors duration-300"
                          />
                        </div>
                        
                        <p className="text-[var(--text-muted)] text-sm mb-1">{highlight.text}</p>
                        <p 
                          className="text-xl font-bold transition-colors duration-300"
                          style={{ color: isActive ? heroConfig.accentColor : 'var(--text-primary)' }}
                        >
                          {highlight.value}
                        </p>
                        
                        {/* Hover Arrow */}
                        <div className={`absolute bottom-4 right-4 transition-all duration-300 ${isActive ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'}`}>
                          <ArrowRight size={16} style={{ color: heroConfig.accentColor }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Stats Bar */}
                <div className="mt-6 p-6 bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className={`text-3xl font-bold bg-gradient-to-r ${heroConfig.gradient} bg-clip-text text-transparent`}>
                        {heroConfig.stats.projects}
                      </p>
                      <p className="text-[var(--text-muted)] text-sm">Projects Delivered</p>
                    </div>
                    <div className="border-x border-[var(--border)]">
                      <p className={`text-3xl font-bold bg-gradient-to-r ${heroConfig.gradient} bg-clip-text text-transparent`}>
                        {heroConfig.stats.satisfaction}
                      </p>
                      <p className="text-[var(--text-muted)] text-sm">Satisfaction</p>
                    </div>
                    <div>
                      <p className={`text-3xl font-bold bg-gradient-to-r ${heroConfig.gradient} bg-clip-text text-transparent`}>
                        {heroConfig.stats.avgDelivery}
                      </p>
                      <p className="text-[var(--text-muted)] text-sm">Avg. Delivery</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-8 px-[7.6923%] border-y border-[var(--border)]" style={{ background: 'var(--bg-card)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F97316]/20 flex items-center justify-center">
                <Star size={20} className="text-[#F97316]" />
              </div>
              <div>
                <p className="text-[var(--text-primary)] font-semibold">4.9/5 Rating</p>
                <p className="text-[var(--text-muted)] text-xs">Based on 200+ reviews</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#10B981]/20 flex items-center justify-center">
                <Shield size={20} className="text-[#10B981]" />
              </div>
              <div>
                <p className="text-[var(--text-primary)] font-semibold">Money Back Guarantee</p>
                <p className="text-[var(--text-muted)] text-xs">30-day satisfaction</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center">
                <Headphones size={20} className="text-[#8B5CF6]" />
              </div>
              <div>
                <p className="text-[var(--text-primary)] font-semibold">24/7 Support</p>
                <p className="text-[var(--text-muted)] text-xs">Always here to help</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                <Rocket size={20} className="text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-[var(--text-primary)] font-semibold">Fast Delivery</p>
                <p className="text-[var(--text-muted)] text-xs">On-time guarantee</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <AnimatedShowcase serviceId={serviceId} />

      {/* Animated How It Works Section */}
      <section className="py-12 px-[7.6923%]" style={{ background: 'var(--bg-dark)' }}>
        <HowItWorksAnimated serviceId={serviceId} />
      </section>

      {/* Deployment Process */}
      <section className="py-16 px-[7.6923%]" style={{ background: 'var(--bg-card)' }}>
        <div className="max-w-7xl mx-auto">
          <div className={`relative bg-gradient-to-r ${heroConfig.gradient} p-[1px] rounded-2xl`}>
            <div className="bg-[var(--bg-card)] rounded-2xl p-8 md:p-12">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${heroConfig.gradient} flex items-center justify-center`}>
                  <Rocket size={24} className="text-white" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)]">
                  Deployment & Delivery
                </h2>
              </div>
              <p className="text-[var(--text-secondary)] leading-relaxed text-lg max-w-4xl">
                {service.deployment_process}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section id="pricing" className="py-16 px-[7.6923%]" style={{ background: 'var(--bg-dark)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border`} style={{ background: `${heroConfig.accentColor}10`, borderColor: `${heroConfig.accentColor}30` }}>
              <span style={{ color: heroConfig.accentColor }} className="text-sm font-semibold">Pricing</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4">
              Choose Your{' '}
              <span className={`bg-gradient-to-r ${heroConfig.gradient} bg-clip-text text-transparent`}>
                Package
              </span>
            </h2>
            <p className="text-[var(--text-muted)] text-lg">Compare features and select the package that fits your needs</p>
          </div>

          {/* Package Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {(service.packages || []).map((pkg, index) => {
              const colors = ['#3B82F6', '#8B5CF6', '#F97316'];
              const color = colors[index % 3];
              
              return (
                <div
                  key={pkg.id}
                  className={`relative bg-[var(--bg-card)] border-2 rounded-2xl transition-all duration-500 hover:-translate-y-3 group ${
                    pkg.popular
                      ? 'border-[#8B5CF6] shadow-xl shadow-[#8B5CF6]/20'
                      : 'border-[var(--border)] hover:border-[var(--border-hover)]'
                  }`}
                >
                  {/* Popular Badge */}
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-2 text-sm font-bold text-white rounded-full" style={{ background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' }}>
                      MOST POPULAR
                    </div>
                  )}

                  <div className="p-8">
                    {/* Discount Badge */}
                    {isOfferActive && (
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#F97316] to-[#EC4899] text-white text-xs font-bold rounded-full">
                          <Zap size={12} fill="currentColor" />
                          {DISCOUNT_PERCENT}% OFF
                        </div>
                        <span className="text-[#10B981] text-xs font-medium">Limited Time!</span>
                      </div>
                    )}

                    {/* Package Header */}
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-3">{pkg.name}</h3>
                      <div className="flex items-baseline gap-2 mb-3">
                        {isOfferActive ? (
                          <>
                            <span className="text-2xl text-[var(--text-muted)] line-through">${pkg.price.toLocaleString()}</span>
                            <span className="text-4xl font-bold text-[#10B981]">${getDiscountedPrice(pkg.price).toLocaleString()}</span>
                          </>
                        ) : (
                          <span className="text-4xl font-bold" style={{ color }}>${pkg.price.toLocaleString()}</span>
                        )}
                        <span className="text-[var(--text-muted)] text-sm">one-time</span>
                      </div>
                      <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                        <Clock size={16} style={{ color }} />
                        <span>{pkg.duration}</span>
                      </div>
                    </div>

                    {/* Ideal For */}
                    <div className="mb-6 pb-6 border-b border-[var(--border)]">
                      <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                        <Users size={18} style={{ color }} />
                        <span className="text-sm font-medium">{pkg.ideal_for}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-8">
                      <ul className="space-y-3">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div 
                              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ background: `${color}20` }}
                            >
                              <Check size={12} style={{ color }} />
                            </div>
                            <span className="text-[var(--text-secondary)] text-sm leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Buy Now Button */}
                    <button
                      onClick={() => buyNow(pkg)}
                      data-testid={`buy-now-${pkg.id}`}
                      className={`w-full py-4 text-lg font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-3 ${
                        pkg.popular
                          ? 'text-white hover:scale-105 hover:shadow-xl'
                          : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] hover:bg-[var(--bg-dark)] border border-[var(--border)]'
                      }`}
                      style={pkg.popular ? { background: `linear-gradient(135deg, ${color}, ${color}CC)` } : {}}
                    >
                      <Zap size={20} />
                      Buy Now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-[7.6923%]" style={{ background: 'var(--bg-card)' }}>
        <div className="max-w-4xl mx-auto">
          <div className={`relative p-[1px] rounded-2xl bg-gradient-to-r ${heroConfig.gradient}`}>
            <div className="bg-[var(--bg-card)] rounded-2xl p-12 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Zap size={32} style={{ color: heroConfig.accentColor }} />
                <h3 className="text-2xl font-bold text-[var(--text-primary)]">Why Choose CLOUD IT?</h3>
              </div>
              <p className="text-[var(--text-secondary)] max-w-3xl mx-auto leading-relaxed">
                Expert team with 10+ years of experience, cutting-edge technology stack, 
                dedicated support, and proven track record of delivering 200+ successful projects. 
                Your success is our mission.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-[7.6923%] text-center">
        <p className="text-[var(--text-muted)] text-lg mb-6">
          Not sure which package is right for you?
        </p>
        <button
          onClick={() => navigate('/book-consultation')}
          className={`px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl inline-flex items-center gap-3 bg-gradient-to-r ${heroConfig.gradient}`}
          style={{ boxShadow: `0 10px 30px ${heroConfig.accentColor}30` }}
        >
          Schedule Free Consultation
          <ArrowRight size={20} />
        </button>
      </section>

      <Footer />
      <Chatbot proactiveDelay={30000} />
      <FloatingOfferBadge />

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </div>
  );
};

export default ServiceDetail;
